import { useEffect, useRef, useState } from 'react';
import { HStack, IconButton, Spinner } from 'native-base';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import { RefreshIcon, Text } from '@pimm/base';

const MS_PER_MIN = 60 * 1_000;
const STALE_THRESHOLD_MS = 5 * 60 * 1_000;

type FreshnessLabelProps = {
  lastUpdatedAt: number | undefined;
  isFetching?: boolean;
  onRefresh: () => void;
};

export const FreshnessLabel = ({ lastUpdatedAt, isFetching, onRefresh }: FreshnessLabelProps) => {
  const { t } = useTranslation();
  const [, forceUpdate] = useState(0);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (!lastUpdatedAt) return;

    const tick = () => {
      forceUpdate(n => n + 1);
      const elapsed = Date.now() - lastUpdatedAt;
      const nextIn = MS_PER_MIN - (elapsed % MS_PER_MIN);
      timeoutRef.current = setTimeout(tick, nextIn);
    };

    const elapsed = Date.now() - lastUpdatedAt;
    timeoutRef.current = setTimeout(tick, MS_PER_MIN - (elapsed % MS_PER_MIN));

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [lastUpdatedAt]);

  if (!lastUpdatedAt) return null;

  const isStale = Date.now() - lastUpdatedAt >= STALE_THRESHOLD_MS;

  return (
    <HStack space={isStale ? 1 : 0} alignItems="center" py={1} pl={3} minH={8}>
      <Text size="sm" color={isStale ? 'amber.700' : 'gray.600'}>
        {moment(lastUpdatedAt).fromNow()}
      </Text>

      <IconButton
        variant="unstyled"
        rounded="lg"
        boxSize={7}
        p={0}
        bg={isStale ? 'gray.50' : 'transparent'}
        opacity={isStale ? 1 : 0.5}
        _hover={{ bg: 'gray.200' }}
        accessibilityLabel={t('store_equipment:refresh')}
        icon={isFetching ? <Spinner boxSize={4} color="gray.500" /> : <RefreshIcon size="16px" color={isStale ? 'gray.600' : 'gray.400'} />}
        onPress={onRefresh}
      />
    </HStack>
  );
};
